import { LINKED_ISSUE_OVERVIEW_ENTITY, LinkedIssueOverviewUpdate } from '@common/interfaces/linkedIssueOverview';
import { LinkedIssueOverviewSyncService } from '@ep-om/project/state/linkedIssueOverview/linkedIssueOverview-sync.service';
import { Injectable } from '@angular/core';
import {
  RealtimeEntitySyncPayload,
  RealtimeEntityUpdatesPayload,
  RealtimeScopeReadyPayload,
  RealtimeScopeSyncStartedPayload,
  RealtimeUpdateOrigin,
} from '@common/api/realtime';
import { EntityResponse } from '@common/api/ack';
import { IBaseEntity } from '@common/interfaces/base';
import { IMetaInterfaces } from '@common/interfaces/metaInterfaces';
import { Topics } from '@common/interfaces/topics';
import { EntityStoreAction, getEntityStoreByName, runEntityStoreAction, transaction } from '@datorama/akita';
import { AuthStore } from '@ep-om/project/auth/auth.store';
import { LastUpdateService } from '@ep-om/project/state/lastUpdate/lastUpdate.service';
import { LastUpdateInfo } from '@ep-om/project/state/lastUpdate/lastUpdate.model';
import { LastUpdateState } from '@ep-om/project/state/lastUpdate/lastUpdate.store';
import { MetaInterfaceServiceProvider } from '@ep-om/project/state/metaInterfaces/metaInterfaces.provider.service';
import { ProjectScopeQuery } from '@ep-om/project/state/projectScope/projectScope.query';
import { toRealtimeV2StoreName } from './realtime-v2-entity-name-map';
import { REALTIME_V2_SCOPE_KEYS } from './realtime-v2.models';
import { RealtimeV2SocketService } from './realtime-v2-socket.service';
import { getRealtimeV2EntityUpdateContext, recordRealtimeV2EntityUpdateContext } from './realtime-v2-update-context';
import { reconcileIncomingStoreEntity } from './entity-reconciliation.service';
import { PersistedStoreRegistry } from './persisted-store-registry.service';
import { isDate } from 'date-fns';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Motivo per cui reconcilePersistedCursors azzera un cursore. L'azione e' sempre la stessa (drop e
 * resync dell'entita'), cambia la diagnosi:
 * - store_not_persisted: lo store non ha una persistState, quindi riparte vuoto ad ogni reload mentre
 *   il cursore no. E' un bug di configurazione (manca @PersistedStoreConfig sullo store), non una race.
 * - hydration_lost: IDB conteneva entita' per lo store all'avvio ma lo store e' vuoto.
 * - entity_missing / entity_stale: lo store e' persistito e ha dati, ma non copre il cursore. E' la
 *   race tra la scrittura del cursore e quella delle entita' per cui il check e' nato.
 */
export type CursorDropReason = 'store_not_persisted' | 'hydration_lost' | 'entity_missing' | 'entity_stale';

@Injectable({
  providedIn: 'root',
})
export class RealtimeV2EntitySyncService {
  // Stream batch-activity: cursor per-design (vedi reconcilePersistedCursors), lastId non e' un'entita'.
  private static readonly BATCH_ACTIVITY_STREAM_STORES = new Set<string>([
    Topics.GLOBAL_BATCH_ACTIVITIES,
    Topics.USER_BATCH_ACTIVITIES,
    Topics.PROJECT_BATCH_ACTIVITIES,
  ]);

  // Stato dello scope globale della sessione corrente. Latch (BehaviorSubject, non evento) cosi'
  // chi attende dopo il ready ne legge subito il valore. false = in sync / non ancora ready.
  // Il runtime lo azzera ad ogni (ri)enterHome e ci si blocca sopra prima di ogni enterProject:
  // non si entra in un progetto finche' le entita' di base (es. metainterfacce) non sono sincronizzate.
  private readonly _globalScopeReady$ = new BehaviorSubject<boolean>(false);
  public readonly globalScopeReady$: Observable<boolean> = this._globalScopeReady$.asObservable();

  public get isGlobalScopeReady(): boolean {
    return this._globalScopeReady$.value;
  }

  /** Azzera lo stato ready dello scope globale (chiamato dal runtime ad ogni (ri)enterHome). */
  public resetGlobalScopeReady(): void {
    this._globalScopeReady$.next(false);
  }

  constructor(
    private lastUpdateService: LastUpdateService,
    private projectScopeQuery: ProjectScopeQuery,
    private authStore: AuthStore,
    private interfaceServiceProvider: MetaInterfaceServiceProvider,
    private socket: RealtimeV2SocketService,
    private persistedStores: PersistedStoreRegistry,
    private overviewSync: LinkedIssueOverviewSyncService,
  ) {}

  applyEntityUpdates(payload: RealtimeEntityUpdatesPayload<IBaseEntity[]>): void {
    const shouldUpdateCursor = this.isDurableScope(payload.scopeKey);
    this.applyEntityBatch(
      payload.scopeKey,
      payload.entityName,
      payload.entities,
      undefined,
      shouldUpdateCursor,
      payload.origin,
    );
  }

  applyEntitySync(payload: RealtimeEntitySyncPayload<IBaseEntity[]>): void {
    const scopeKey = payload.data.scopeKey;
    const shouldUpdateCursor = !this.isTransientIssueScope(scopeKey);
    this.applyEntityBatch(
      scopeKey,
      payload.data.entityName,
      payload.data.entities,
      payload.remaining,
      shouldUpdateCursor,
    );
  }

  applyEntityResponse(scopeKey: string, response: EntityResponse<IBaseEntity[]>): void {
    this.applyEntityBatch(scopeKey, response.entityName, response.entities, undefined, false);
  }

  updateCursorFromEntity(
    scopeKey: string,
    entityName: string,
    entity: Pick<IBaseEntity, 'id' | 'updatedAt'>,
    remaining?: number,
    storedEntity?: Pick<IBaseEntity, 'id' | 'updatedAt'>,
  ): void {
    if (!entity?.id || !entity?.updatedAt) {
      return;
    }

    const update = {
      // Il cursor deve conservare tutta la precisione disponibile: un valore troncato (formatISO
      // arrotonda al secondo) resta indietro rispetto alla riga sul DB e il server rimanda le
      // stesse entita' ad ogni riconnessione.
      lastId: entity.id,
      lastUpdate: this.toIsoString(entity.updatedAt),
      // L'ancora in-store avanza solo con un'entita' viva: un batch di sole cancellazioni
      // conserva l'ancora precedente (setLastUpdate fa il merge sull'entry esistente).
      // Un'entita' viva appena upsertata rende anche lo store non-vuoto.
      ...(storedEntity?.id && storedEntity?.updatedAt && {
        lastStoredId: storedEntity.id,
        lastStoredUpdate: this.toIsoString(storedEntity.updatedAt),
        storeEmptied: false,
      }),
      ...(remaining !== undefined && { remaining }),
    };
    const projectId = this.getProjectId(scopeKey);
    const storeName = toRealtimeV2StoreName(entityName);

    if (projectId) {
      this.lastUpdateService.setChildLastUpdate(projectId, storeName, update);
      return;
    }

    this.lastUpdateService.setLastUpdate(storeName, update);
  }

  /**
   * Azzera il cursore globale degli appuntamenti: al prossimo enterHome il BE li ri-streamma da zero
   * come in un first-sync (usato per l'action di batch-activity `appointment_resync`).
   */
  resetAppointmentCursor(): void {
    this.lastUpdateService.clearCursor(Topics.APPOINTMENT);
  }

  markScopeSyncStarted(payload: RealtimeScopeSyncStartedPayload): void {
    if (!this.isDurableScope(payload.scopeKey)) {
      return;
    }

    // Lo scope globale riparte in sync (anche dopo un reconnect): torna non-ready finche' non arriva
    // lo scope_ready. Evita che un enterProject proceda su un ready stale della connessione precedente.
    if (this.isGlobalScope(payload.scopeKey)) {
      this._globalScopeReady$.next(false);
    }

    this.markScopeEntities(payload.scopeKey, payload.entities, 'first_sync');
  }

  markScopeReady(payload: RealtimeScopeReadyPayload): void {
    if (!this.isDurableScope(payload.scopeKey)) {
      return;
    }

    this.markScopeEntities(payload.scopeKey, payload.entities, 'live');

    if (this.isGlobalScope(payload.scopeKey)) {
      this._globalScopeReady$.next(true);
    }
  }

  /**
   * Verifica che ogni cursor persistito sia coperto dai dati realmente presenti nello store.
   * Il cursor e' valido se il watermark (lastId/lastUpdate = ultima entita' RICEVUTA) O
   * l'ancora in-store (lastStoredId/lastStoredUpdate = ultima entita' viva) sono coperti,
   * O se lo store e' stato svuotato di proposito (storeEmptied): il watermark puo'
   * legittimamente puntare a un'entita' cancellata/archiviata (rimossa dallo store) senza
   * che il cursor sia corrotto, e in quel caso garantisce l'ancora.
   * Se nessuno dei due e' coperto il cursor e' avanti rispetto ai dati - tipicamente dopo
   * un refresh in cui la scrittura del cursor e quella dell'entita' (persistState
   * indipendenti, debounced) non sono atterrate insieme. In quel caso azzeriamo il cursor:
   * meglio un resync dell'entita' che un gap permanente.
   * Prima di azzerare classifichiamo il motivo (vedi CursorDropReason): uno store senza
   * persistState o con idratazione persa viene loggato come errore, perche' il resync ad ogni
   * avvio e' il sintomo di un bug e non di una race.
   * Lookup O(1) per id, nessuno scan dello store.
   * I cursor degli stream batch-activity sono per-design (il backend distingue il login
   * fresco dall'assenza di cursor) e il loro lastId non e' un'entita': vengono saltati.
   */
  reconcilePersistedCursors(): void {
    const lastUpdates = this.lastUpdateService.query.getValue() as LastUpdateState;
    for (const [key, info] of Object.entries(lastUpdates)) {
      if (!info?.lastId || !info?.lastUpdate) {
        continue;
      }

      const storeName = this.storeNameFromCursorKey(key);
      if (RealtimeV2EntitySyncService.BATCH_ACTIVITY_STREAM_STORES.has(storeName)) {
        continue;
      }

      const mismatch = this.cursorStoreMismatch(storeName, info);
      if (!mismatch) {
        continue;
      }

      this.logCursorDrop(key, mismatch, info);
      this.lastUpdateService.clearCursor(key);
    }
  }

  private logCursorDrop(key: string, reason: CursorDropReason, info: LastUpdateInfo): void {
    const details = {
      key,
      reason,
      lastId: info.lastId,
      lastUpdate: info.lastUpdate,
      lastStoredId: info.lastStoredId,
      lastStoredUpdate: info.lastStoredUpdate,
      storeEmptied: info.storeEmptied,
    };

    switch (reason) {
      case 'store_not_persisted':
        // TODO: portare fuori dalla console (telemetria) - un errore di configurazione che si
        // manifesta solo come resync ad ogni avvio rischia di restare invisibile.
        console.error('[REALTIME-V2] dropping cursor: store has no persistState, it restarts empty at every reload and resyncs from scratch (use @PersistedStoreConfig on the store)', details);
        return;
      case 'hydration_lost':
        console.error('[REALTIME-V2] dropping cursor: IDB had entities for this store at boot but the store is empty (hydration lost)', details);
        return;
      default:
        console.warn('[REALTIME-V2] dropping cursor ahead of stored data', details);
    }
  }

  /**
   * Da chiamare dopo una rimozione di entita' dallo store (cancellazioni in un batch o
   * batch activity remove_*): se la rimozione ha portato via l'entita' ancorata dal cursore,
   * ri-ancora sull'entita' residua piu' recente (updatedAt max, unico scan O(n) e solo in
   * questo caso raro). Se lo store resta vuoto alza storeEmptied: reconcilePersistedCursors
   * sapra' che la mancanza dell'ancora e' voluta e conservera' il cursore invece di forzare
   * un resync completo.
   */
  reanchorCursorAfterLocalRemoval(storeName: string, projectId?: string | null): void {
    const key = projectId ? this.lastUpdateService.calculateChildKey(projectId, storeName) : storeName;
    const info = (this.lastUpdateService.query.getValue() as LastUpdateState)[key];
    if (!info?.lastStoredId) {
      return;
    }

    let entities: Record<string, IBaseEntity> | undefined;
    try {
      if (this.findEntityById(storeName, info.lastStoredId)) {
        return;
      }
      entities = getEntityStoreByName<any>(storeName).getValue()?.entities;
    } catch (_) {
      return;
    }

    let newAnchor: IBaseEntity | undefined;
    let newAnchorTime = -Infinity;
    for (const entity of Object.values(entities ?? {}) as IBaseEntity[]) {
      const time = this.toEpoch(entity?.updatedAt);
      if (entity?.id && time !== undefined && time > newAnchorTime) {
        newAnchor = entity;
        newAnchorTime = time;
      }
    }

    const update = newAnchor
      ? { lastStoredId: newAnchor.id, lastStoredUpdate: this.toIsoString(newAnchor.updatedAt), storeEmptied: false }
      : { lastStoredId: undefined, lastStoredUpdate: undefined, storeEmptied: true };

    if (projectId) {
      this.lastUpdateService.setChildLastUpdate(projectId, storeName, update);
      return;
    }

    this.lastUpdateService.setLastUpdate(storeName, update);
  }

  @transaction()
  private applyEntityBatch(
    scopeKey: string,
    entityName: string,
    entities: IBaseEntity[],
    remaining?: number,
    updateCursor = true,
    origin?: RealtimeUpdateOrigin,
  ): void {
    if (!entityName || !entities?.length) {
      return;
    }

    if (entityName === LINKED_ISSUE_OVERVIEW_ENTITY) {
      this.overviewSync.apply(scopeKey, entities as unknown as LinkedIssueOverviewUpdate[]);
      return;
    }
    const storeName = toRealtimeV2StoreName(entityName);
    // Lo store accetta solo versioni nuove: cosi' un messaggio arrivato in ritardo
    // non puo' riportare indietro l'entita'.
    const data = entities.filter((entity) => this.shouldApplyEntity(storeName, entity));

    if (data.length) {
      this.recordEntityUpdateContexts(entityName, data, origin);

      const toBeRemoved = data
        .filter((entity) => !!entity.deletedAt || !!entity.archivedAt)
        .map((entity) => entity.id);
      const toBeUpserted = data.filter((entity) => !entity.deletedAt && !entity.archivedAt);

      if (toBeUpserted.length > 0) {
        // Prima dell'upsert riconciliamo il self-echo: conserviamo i campi che ho gia' sovrascritto
        // con una mutation locale piu' recente, cosi' l'eco di un mio cambio intermedio non regredisce
        // il valore corrente (es. il salto di stato issue in board). Il version-gate da solo non basta.
        const reconciled = toBeUpserted.map((entity) => this.reconcileSelfEcho(storeName, entity));
        runEntityStoreAction(
          storeName,
          EntityStoreAction.UpsertManyEntities,
          (upsertManyEntities) => upsertManyEntities(reconciled),
        );
        console.log(`[SYNC-SERVICE] - ${storeName} - upserted ${reconciled.length} entitites`)
      }

      if (toBeRemoved.length > 0) {
        runEntityStoreAction(
          storeName,
          EntityStoreAction.RemoveEntities,
          (removeEntities) => removeEntities(toBeRemoved),
        );
      }

      this.applyEntitySideEffects(storeName, data);
    }

    // Il cursor avanza al watermark delle entita' RICEVUTE, non solo di quelle scritte.
    // Se il version gate scarta tutto (entita' gia' presenti a versione >=), saltare il
    // cursor lo lascerebbe indietro e il server ri-spedirebbe le stesse entita' ad ogni
    // sync all'infinito. Riceverle == averle gia' consumate fino a quel watermark.
    if (updateCursor) {
      this.updateCursor(scopeKey, storeName, entities, remaining);

      // Un batch con cancellazioni/archiviazioni puo' aver rimosso proprio l'entita'
      // ancorata dal cursore: ri-validiamo l'ancora (check O(1), scan solo se serve).
      if (entities.some((entity) => !!entity.deletedAt || !!entity.archivedAt)) {
        this.reanchorCursorAfterLocalRemoval(storeName, this.getProjectId(scopeKey));
      }
    }
  }

  private shouldApplyEntity(storeName: string, incoming: IBaseEntity): boolean {
    const current = this.getCurrentEntity(storeName, incoming.id);
    const incomingVersion = this.getEntityVersion(incoming);
    const currentVersion = this.getEntityVersion(current);

    return incomingVersion === undefined
      || currentVersion === undefined
      || incomingVersion > currentVersion;
  }

  private getCurrentEntity(storeName: string, entityId: string): IBaseEntity | undefined {
    try {
      return this.findEntityById(storeName, entityId);
    } catch (_) {
      return undefined;
    }
  }

  /**
   * Ritrova l'entita' per il suo `id` indipendentemente dall'idKey dello store.
   * I cursor referenziano sempre `entity.id`, ma alcuni store sono indicizzati su una
   * chiave diversa (es. ProjectScope -> projectId): in quel caso la mappa `entities` non
   * e' chiavata per id e il lookup O(1) fallirebbe, quindi facciamo uno scan (set piccolo).
   * Propaga l'eccezione di accesso allo store: i chiamanti distinguono "store non gestito"
   * (non droppare il cursor) da "entita' assente" (cursor avanti rispetto ai dati).
   */
  private findEntityById(storeName: string, entityId: string): IBaseEntity | undefined {
    const store = getEntityStoreByName<any>(storeName);
    const entities = store.getValue()?.entities;
    if (!entities) {
      return undefined;
    }

    const idKey = store.akitaConfig?.idKey ?? store.idKey ?? 'id';
    if (idKey === 'id') {
      return entities[entityId];
    }

    return Object.values(entities).find((entity: any) => entity?.id === entityId) as IBaseEntity | undefined;
  }

  private storeNameFromCursorKey(key: string): string {
    // Le child key sono `PRJ_<projectId>_<storeName>`; il projectId e' un uuid (niente underscore).
    const childMatch = /^PRJ_[^_]+_(.+)$/.exec(key);
    return childMatch ? childMatch[1] : key;
  }

  /**
   * Verifica il cursore contro lo store (OR-check). Ritorna null se e' coperto, cioe' se:
   * - il watermark lastId/lastUpdate e' coperto (comportamento storico, vale anche per i
   *   cursor legacy senza ancora); oppure
   * - l'ancora in-store lastStoredId/lastStoredUpdate e' coperta (il watermark puntava a
   *   una cancellazione); oppure
   * - storeEmptied: lo store e' stato svuotato di proposito da una rimozione locale.
   * Altrimenti ritorna il motivo del mismatch (vedi CursorDropReason). La persistenza viene
   * verificata per prima perche' vale qualunque sia il contenuto dello store ed e' la diagnosi
   * azionabile; hydration_lost richiede lo store vuoto (uno store con dati a cui manca solo
   * l'entita' del cursore e' cursore-avanti, non idratazione persa); per il resto vale il
   * motivo dell'ancora se presente, piu' informativa.
   */
  private cursorStoreMismatch(storeName: string, info: LastUpdateInfo): CursorDropReason | null {
    try {
      const watermarkState = this.entityBackedState(storeName, info.lastId, info.lastUpdate);
      if (watermarkState === 'backed') {
        return null;
      }

      const anchorState = info.lastStoredId
        ? this.entityBackedState(storeName, info.lastStoredId, info.lastStoredUpdate)
        : undefined;
      if (anchorState === 'backed') {
        return null;
      }

      if (info.storeEmptied) {
        return null;
      }

      if (!this.persistedStores.isPersisted(storeName)) {
        return 'store_not_persisted';
      }

      if (this.isStoreEmpty(storeName) && this.persistedStores.persistedEntityCountAtBoot(storeName) > 0) {
        return 'hydration_lost';
      }

      return (anchorState ?? watermarkState) === 'stale' ? 'entity_stale' : 'entity_missing';
    } catch (_) {
      // Store non gestito o non ancora caricato (es. meta-interface): non possiamo
      // verificare, quindi non droppiamo il cursor.
      return null;
    }
  }

  private isStoreEmpty(storeName: string): boolean {
    const ids = getEntityStoreByName<any>(storeName).getValue()?.ids;
    return !ids?.length;
  }

  private entityBackedState(storeName: string, entityId: string, lastUpdate?: string): 'backed' | 'missing' | 'stale' {
    const entity = this.findEntityById(storeName, entityId);
    if (!entity) {
      return 'missing';
    }

    const entityTime = this.toEpoch(entity.updatedAt);
    const cursorTime = this.toEpoch(lastUpdate);
    if (entityTime === undefined || cursorTime === undefined) {
      // Date non confrontabili: la presenza dell'entita' (lookup by id) basta.
      return 'backed';
    }

    return entityTime >= cursorTime ? 'backed' : 'stale';
  }

  private toEpoch(value: unknown): number | undefined {
    if (!value) {
      return undefined;
    }

    const time = value instanceof Date ? value.getTime() : new Date(value as string).getTime();
    return Number.isFinite(time) ? time : undefined;
  }

  private getEntityVersion(entity?: IBaseEntity): number | undefined {
    const version = entity?.version;
    if (version === undefined || version === null) {
      return undefined;
    }

    const numericVersion = Number(version);
    return Number.isFinite(numericVersion) ? numericVersion : undefined;
  }

  private recordEntityUpdateContexts(
    entityName: string,
    entities: IBaseEntity[],
    origin?: RealtimeUpdateOrigin,
  ): void {
    // Conserviamo l'origine insieme alla versione applicata.
    // Quando Angular aggiorna la form, puo' decidere campo per campo cosa patchare.
    const sameOrigin = !!origin?.socketId && origin.socketId === this.socket.currentSocketId;

    for (const entity of entities) {
      recordRealtimeV2EntityUpdateContext({
        entityName,
        entityId: entity.id,
        version: this.getEntityVersion(entity),
        origin,
        sameOrigin,
      });
    }
  }

  /**
   * Riconcilia un self-echo contro il valore corrente dello store, preservando i campi coperti da
   * una mutation locale piu' recente. La version resta quella in arrivo (mai marcata come mutation
   * locale), cosi' il gate monotono e gli echi successivi continuano a funzionare.
   *
   * NON fa ack del registro: la chiusura delle mutation locali resta di competenza del form
   * (reconcileShouldSkip), che con la soglia >= protegge anche i tasti non ancora salvati nel gap
   * del debounce. Un ack store-side qui li scoprirebbe (regressione sugli update continui di form).
   */
  private reconcileSelfEcho(storeName: string, entity: IBaseEntity): IBaseEntity {
    const ctx = getRealtimeV2EntityUpdateContext(entity.id, this.getEntityVersion(entity));
    if (!ctx?.sameOrigin) {
      return entity;
    }

    const current = this.getCurrentEntity(storeName, entity.id);
    return reconcileIncomingStoreEntity(
      entity.id,
      entity as unknown as Record<string, unknown>,
      current as unknown as Record<string, unknown> | undefined,
      ctx,
    ) as unknown as IBaseEntity;
  }

  private applyEntitySideEffects(entityName: string, entities?: IBaseEntity[]): void {
    if (entityName === Topics.INTERFACES) {
      this.applyMetaInterfaceSideEffects(entities as IMetaInterfaces[]);
    }

    if (entityName === Topics.PROJECT_SCOPES) {
      this.authStore.updateProjectRolesFromProjectScopes(this.projectScopeQuery.getAll());
    }
  }

  private applyMetaInterfaceSideEffects(entities: IMetaInterfaces[] = []): void {
    for (const item of entities) {
      if (!item?.tableName) {
        continue;
      }

      if (!item.deletedAt && !item.archivedAt) {
        this.interfaceServiceProvider.addStore({ name: item.tableName, idKey: 'id' });
        continue;
      }

      this.interfaceServiceProvider.removeStore(item.tableName);
    }
  }

  private updateCursor(
    scopeKey: string,
    entityName: string,
    entities: IBaseEntity[],
    remaining?: number,
  ): void {
    // L'ancora in-store e' l'ultima entita' viva del batch: le entita' cancellate/archiviate
    // vengono rimosse dallo store e non possono garantire il cursore al reconcile.
    let storedEntity: IBaseEntity | undefined;
    for (let i = entities.length - 1; i >= 0; i--) {
      if (!entities[i].deletedAt && !entities[i].archivedAt) {
        storedEntity = entities[i];
        break;
      }
    }

    this.updateCursorFromEntity(scopeKey, entityName, entities.at(-1), remaining, storedEntity);
  }

  private toIsoString(value: IBaseEntity['updatedAt']): string {
    return isDate(value) ? (value as unknown as Date).toISOString() : (value as string);
  }

  private markScopeEntities(
    scopeKey: string,
    entityNames: string[],
    status: 'first_sync' | 'live',
  ): void {
    const projectId = this.getProjectId(scopeKey);
    entityNames.forEach((entityName) => {
      const storeName = toRealtimeV2StoreName(entityName);
      if (status === 'first_sync') {
        this.lastUpdateService.setStoreInSync(storeName, projectId);
        return;
      }

      this.lastUpdateService.setStoreLive(storeName, projectId);
      this.applyEntitySideEffects(storeName);
    });
  }

  private isGlobalScope(scopeKey: string): boolean {
    return scopeKey === REALTIME_V2_SCOPE_KEYS.GLOBAL;
  }

  private isDurableScope(scopeKey: string): boolean {
    return scopeKey === REALTIME_V2_SCOPE_KEYS.GLOBAL
      || scopeKey?.startsWith(REALTIME_V2_SCOPE_KEYS.PROJECT_PREFIX);
  }

  private getProjectId(scopeKey: string): string | null {
    if (!scopeKey?.startsWith(REALTIME_V2_SCOPE_KEYS.PROJECT_PREFIX)) {
      return null;
    }

    return scopeKey.substring(REALTIME_V2_SCOPE_KEYS.PROJECT_PREFIX.length);
  }

  private isTransientIssueScope(scopeKey: string): boolean {
    return scopeKey?.startsWith(REALTIME_V2_SCOPE_KEYS.ISSUE_PREFIX)
      || scopeKey?.startsWith(REALTIME_V2_SCOPE_KEYS.LINKED_PREVIEW_PREFIX);
  }
}
